import React, { useEffect, useState } from "react";
import { exportTrialBalanceToExcel } from "./excelExportUtils";
import ExportExcelButton from "./ExportExcelButton";

function TrialBalanceYTD(props) {
  let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";

  const { datagetTrialBalanceReport, parentDetails, selected_mode_table_structure } = props;
  const [expandedNodes, setExpandedNodes] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  // Expand all nodes and groups by default
  useEffect(() => {
    if (datagetTrialBalanceReport && datagetTrialBalanceReport.length > 0) {
      const allExpandedNodes = {};
      const allExpandedGroups = {};

      datagetTrialBalanceReport.forEach((row) => {
        allExpandedNodes[row.NODE] = true;
        const groupKey = `${row.NODE}_${row["GROUP CODE"]}`;
        allExpandedGroups[groupKey] = true;
      });

      setExpandedNodes(allExpandedNodes);
      setExpandedGroups(allExpandedGroups);
    }
  }, [datagetTrialBalanceReport]);

  const handleExportExcel = () => {
    const selectedModeMeta = selected_mode_table_structure.find(
      (item) => item.mode_table_name === parentDetails.mode_list_array.find((m) => m.is_selected)?.mode_name
    );
    const visibleColumns = selectedModeMeta?.mode_table_columns.filter(
      (col) => col.column_key_from_api !== "NODE" && col.column_key_from_api !== "GROUP CODE"
    ) || [];

    exportTrialBalanceToExcel({
      data: datagetTrialBalanceReport,
      columns: visibleColumns,
      groupKeys: ["NODE", "GROUP CODE"],
      valueKeys: ["OPENDr", "OPENCr", "DEBIT", "CREDIT", "BALANCE"],
      deriveFromBalance: false,
      fileName: selectedModeMeta?.mode_table_name || "TrialBalanceYTD",
      sheetName: "Trial Balance YTD",
    });
  };

  // Shared layout and grand total calculations
  const selectedModeMeta = selected_mode_table_structure.find(
    (item) => item.mode_table_name === parentDetails.mode_list_array.find((m) => m.is_selected)?.mode_name
  );

  const visibleColumns = selectedModeMeta?.mode_table_columns.filter(
    (col) => col.column_key_from_api !== "NODE" && col.column_key_from_api !== "GROUP CODE"
  ) || [];

  const visibleColumnsCount = visibleColumns.length || 2;
  const labelColSpan = visibleColumnsCount - 5 > 0 ? visibleColumnsCount - 5 : 1;

  const grandTotal = (datagetTrialBalanceReport || []).reduce(
    (acc, r) => ({
      OPENDr: acc.OPENDr + (r.OPENDr || 0),
      OPENCr: acc.OPENCr + (r.OPENCr || 0),
      DEBIT: acc.DEBIT + (r.DEBIT || 0),
      CREDIT: acc.CREDIT + (r.CREDIT || 0),
      BALANCE: acc.BALANCE + (r.BALANCE || 0),
    }),
    { OPENDr: 0, OPENCr: 0, DEBIT: 0, CREDIT: 0, BALANCE: 0 }
  );

  return (
    <div className="min-h-screen bg-gray-200 py-6 px-4">
      <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-none border border-gray-300 px-4 pt-4 pb-4 h-[85vh] flex flex-col">
        
        {/* FIXED TOP HEADER */}
        <div className="flex-none">
          <div style={{ display: "flex", justifyContent: "center" }}>
            {apiPaths_from_local_storage[0]?.REP_HEADER?.includes("data:image/") && (
              <img
                style={{ maxWidth: "65%" }}
                src={apiPaths_from_local_storage[0].REP_HEADER}
                alt="Report Header"
              />
            )}
          </div>

          <div style={{
            fontSize: '18px',
            fontWeight: '500',
            color: '#0f172a',
            textAlign: 'center',
            textTransform: 'uppercase',
            margin: '12px 0',
            padding: '8px 16px',
            backgroundColor: '#f8fafc',
            borderRadius: '6px'
          }}>
            {selectedModeMeta?.mode_table_name}
          </div>

          {/* <ExportExcelButton onClick={handleExportExcel} /> */}
        </div>

        {/* SCROLLABLE TABLE BODY */}
        <div className="flex-1 overflow-y-auto overflow-x-auto border-b border-gray-200 mt-3">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="border-b-2 border-gray-900 sticky top-0 bg-white z-10">
              <tr>
                {visibleColumns.map((item, i) => (
                  <th
                    key={i}
                    className={`px-4 py-2 text-xs font-bold text-gray-900 uppercase tracking-wider ${
                      ["OPENDr", "OPENCr", "DEBIT", "CREDIT", "BALANCE"].includes(item.column_key_from_api)
                        ? "text-right"
                        : "text-left"
                    }`}
                  >
                    {item.column_name}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(() => {
                const groupedData = (datagetTrialBalanceReport || []).reduce((acc, row) => {
                  const nodeKey = row.NODE || "UNKNOWN NODE";
                  const groupKey = row["GROUP CODE"] ?? row["GROUP_CODE"] ?? "UNKNOWN GROUP";

                  if (!acc[nodeKey]) acc[nodeKey] = {};
                  if (!acc[nodeKey][groupKey]) acc[nodeKey][groupKey] = [];
                  acc[nodeKey][groupKey].push(row);
                  return acc;
                }, {});

                return (
                  <>
                    {Object.entries(groupedData).map(([node, groups], nodeIndex) => {
                      const nodeSubtotal = Object.values(groups)
                        .flat()
                        .reduce(
                          (acc, r) => ({
                            OPENDr: acc.OPENDr + (r.OPENDr || 0),
                            OPENCr: acc.OPENCr + (r.OPENCr || 0),
                            DEBIT: acc.DEBIT + (r.DEBIT || 0),
                            CREDIT: acc.CREDIT + (r.CREDIT || 0),
                            BALANCE: acc.BALANCE + (r.BALANCE || 0),
                          }),
                          { OPENDr: 0, OPENCr: 0, DEBIT: 0, CREDIT: 0, BALANCE: 0 }
                        );

                      return (
                        <React.Fragment key={nodeIndex}>
                          {/* NODE HEADER */}
                          <tr
                            className="border-t border-gray-200 cursor-pointer hover:bg-gray-50"
                            onClick={() =>
                              setExpandedNodes((prev) => ({ ...prev, [node]: !prev[node] }))
                            }
                          >
                            <td
                              colSpan={visibleColumnsCount}
                              className="px-4 py-2 font-bold text-gray-900 text-sm flex items-center gap-2 uppercase tracking-wide"
                            >
                              <span
                                className={`transition-transform ${expandedNodes[node] ? "rotate-90" : ""}`}
                              >
                                ▶
                              </span>
                              {node}
                            </td>
                          </tr>

                          {/* GROUPS UNDER NODE */}
                          {expandedNodes[node] &&
                            Object.entries(groups).map(([groupCode, rows], groupIndex) => {
                              const groupKey = `${node}_${groupCode}`;

                              const groupSubtotal = rows.reduce(
                                (acc, r) => ({
                                  OPENDr: acc.OPENDr + (r.OPENDr || 0),
                                  OPENCr: acc.OPENCr + (r.OPENCr || 0),
                                  DEBIT: acc.DEBIT + (r.DEBIT || 0),
                                  CREDIT: acc.CREDIT + (r.CREDIT || 0),
                                  BALANCE: acc.BALANCE + (r.BALANCE || 0),
                                }),
                                { OPENDr: 0, OPENCr: 0, DEBIT: 0, CREDIT: 0, BALANCE: 0 }
                              );

                              return (
                                <React.Fragment key={groupIndex}>
                                  {/* GROUP HEADER */}
                                  <tr
                                    className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                                    onClick={() =>
                                      setExpandedGroups((prev) => ({
                                        ...prev,
                                        [groupKey]: !prev[groupKey],
                                      }))
                                    }
                                  >
                                    <td
                                      colSpan={visibleColumnsCount}
                                      className="px-6 py-2 font-semibold text-gray-800 text-sm flex items-center gap-2 tracking-wide pl-8"
                                    >
                                      <span
                                        className={`transition-transform ${expandedGroups[groupKey] ? "rotate-90" : ""}`}
                                      >
                                        ▶
                                      </span>
                                      {groupCode}
                                    </td>
                                  </tr>

                                  {/* ROWS UNDER GROUP */}
                                  {expandedGroups[groupKey] &&
                                    rows.map((row, i) => (
                                      <tr key={i} className="hover:bg-gray-50">
                                        {visibleColumns.map((col, ci) => {
                                          const key = col.column_key_from_api;
                                          const isNumeric = ["OPENDr", "OPENCr", "DEBIT", "CREDIT", "BALANCE"].includes(key);
                                          const displayValue = row[key];

                                          return (
                                            <td
                                              key={ci}
                                              className={`px-4 py-2 text-sm ${
                                                isNumeric
                                                  ? "text-right tabular-nums font-mono"
                                                  : "text-left text-gray-700 pl-12"
                                              } ${
                                                key === "DEBIT" || key === "OPENDr"
                                                  ? "text-blue-700"
                                                  : key === "CREDIT" || key === "OPENCr"
                                                  ? "text-emerald-700"
                                                  : key === "BALANCE"
                                                  ? "text-gray-900 font-bold"
                                                  : ""
                                              }`}
                                            >
                                              {isNumeric && typeof displayValue === "number"
                                                ? displayValue.toFixed(2)
                                                : displayValue}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}

                                  {/* GROUP SUBTOTAL */}
                                  {expandedGroups[groupKey] && (
                                    <tr className="border-t border-dashed border-gray-300 font-medium text-sm">
                                      <td colSpan={labelColSpan} className="px-4 py-1.5 italic text-gray-500 text-xs pl-8">
                                        Subtotal — {groupCode}
                                      </td>
                                      <td className="px-4 py-1.5 text-right tabular-nums font-mono text-blue-700 text-xs">
                                        {groupSubtotal.OPENDr.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right tabular-nums font-mono text-emerald-700 text-xs">
                                        {groupSubtotal.OPENCr.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right tabular-nums font-mono text-blue-700 text-xs">
                                        {groupSubtotal.DEBIT.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right tabular-nums font-mono text-emerald-700 text-xs">
                                        {groupSubtotal.CREDIT.toFixed(2)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right tabular-nums font-mono text-gray-900 text-xs font-bold">
                                        {groupSubtotal.BALANCE.toFixed(2)}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}

                          {/* NODE SUBTOTAL */}
                          {expandedNodes[node] && (
                            <tr className="border-t border-gray-300 font-semibold text-sm bg-gray-50">
                              <td colSpan={labelColSpan} className="px-4 py-2 italic text-gray-700 text-xs uppercase tracking-wide">
                                Node Total — {node}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-blue-800 text-xs">
                                {nodeSubtotal.OPENDr.toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-emerald-800 text-xs">
                                {nodeSubtotal.OPENCr.toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-blue-800 text-xs">
                                {nodeSubtotal.DEBIT.toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-emerald-800 text-xs">
                                {nodeSubtotal.CREDIT.toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-gray-900 text-xs font-bold">
                                {nodeSubtotal.BALANCE.toFixed(2)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* FIXED BOTTOM FOOTER */}
        <div className="flex-none pt-2 bg-white">
          <table className="min-w-full text-sm">
            <tbody>
              <tr className="font-bold text-sm" style={{ borderTop: "3px double #111827" }}>
                <td colSpan={labelColSpan} className="px-4 py-2.5 text-gray-900 uppercase tracking-wide text-xs">
                  Grand Total
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-mono text-blue-900">
                  {grandTotal.OPENDr.toFixed(2)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-mono text-emerald-900">
                  {grandTotal.OPENCr.toFixed(2)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-mono text-blue-900">
                  {grandTotal.DEBIT.toFixed(2)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-mono text-emerald-900">
                  {grandTotal.CREDIT.toFixed(2)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-mono text-gray-950">
                  {grandTotal.BALANCE.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="mt-2">
            <div className="border-t border-gray-300" />
            <div className="mt-3 flex justify-between items-center">
              <p className="text-xs text-gray-400">
                Generated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default TrialBalanceYTD;